import { ShareIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { formatFileSize, getFileExtension } from "../../lib/utils/file";
import type { Document } from "../../types/document";
import styles from "./ShareDocumentModal.module.css";

type ShareMethod = "email" | "sms" | "whatsapp";

interface ShareDocumentModalProps {
	document: Document | null;
	isOpen: boolean;
	onClose: () => void;
	t: (key: string) => string;
}

export function ShareDocumentModal({
	document: doc,
	isOpen,
	onClose,
	t,
}: ShareDocumentModalProps) {
	const { logEvent } = useAnalytics();
	const [method, setMethod] = useState<ShareMethod>("email");
	const [recipient, setRecipient] = useState("");
	const [note, setNote] = useState("");
	useEffect(() => {
		if (!isOpen) {
			setMethod("email");
			setRecipient("");
			setNote("");
		}
	}, [isOpen]);

	const handleSend = useCallback(() => {
		if (!doc || !recipient.trim()) return;

		logEvent("file_share_sent", undefined, {
			documentId: doc.id,
			method,
		});
		toast.info(t("share.comingSoon"));
		onClose();
	}, [doc, recipient, method, logEvent, t, onClose]);

	if (!doc) return null;

	const fileExtension = getFileExtension(doc.mimeType, doc.name);
	const fileSize = formatFileSize(doc.fileSize);
	const uploadDate = format(new Date(doc.uploadedAt), "MMM d, yyyy");

	const recipientLabel =
		method === "email" ? t("share.recipientEmail") : t("share.recipientPhone");
	const recipientPlaceholder =
		method === "email"
			? t("share.emailPlaceholder")
			: t("share.phonePlaceholder");

	return (
		<AnimatePresence>
			{isOpen && (
				<>
					<motion.div
						className={styles.overlay}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						onClick={onClose}
					/>
					<div className={styles.container}>
						<motion.div
							className={styles.panel}
							initial={{ y: "100%" }}
							animate={{ y: 0 }}
							exit={{ y: "100%" }}
							transition={{
								type: "spring",
								damping: 30,
								stiffness: 300,
							}}
						>
							<div className={styles.header}>
								<div className={styles.shareIconCircle}>
									<ShareIcon className={styles.shareIconHeader} />
								</div>
								<h2 className={styles.title}>{t("share.title")}</h2>
								<button
									type="button"
									className={styles.closeButton}
									onClick={onClose}
								>
									<XMarkIcon className={styles.closeIcon} />
								</button>
							</div>

							<div className={styles.fileCard}>
								<div className={styles.fileNameRow}>
									<span className={styles.fileName}>{doc.name}</span>
									<span className={styles.typeBadge}>{fileExtension}</span>
								</div>
								<span className={styles.fileDetails}>
									{fileSize} &middot; {uploadDate}
								</span>
							</div>

							<div className={styles.fieldGroup}>
								<p className={styles.sectionLabel}>{t("share.shareThrough")}</p>
								<div className={styles.methodTabs}>
									{(["email", "sms", "whatsapp"] as const).map((m) => (
										<button
											key={m}
											type="button"
											className={`${styles.methodTab} ${method === m ? styles.methodTabActive : ""}`}
											onClick={() => {
												setMethod(m);
												setRecipient("");
											}}
										>
											{t(`share.method.${m}`)}
										</button>
									))}
								</div>
							</div>

							<div className={styles.fieldGroup}>
								<label htmlFor="share-recipient" className={styles.fieldLabel}>
									{recipientLabel}
								</label>
								<input
									id="share-recipient"
									type={method === "email" ? "email" : "tel"}
									className={styles.input}
									placeholder={recipientPlaceholder}
									value={recipient}
									onChange={(e) => setRecipient(e.target.value)}
								/>
							</div>

							<div className={styles.fieldGroup}>
								<label htmlFor="share-note" className={styles.fieldLabel}>
									{t("share.noteLabel")}
								</label>
								<textarea
									id="share-note"
									className={styles.textarea}
									placeholder={t("share.notePlaceholder")}
									value={note}
									onChange={(e) => setNote(e.target.value)}
								/>
							</div>

							<button
								type="button"
								className={styles.sendButton}
								onClick={handleSend}
								disabled={!recipient.trim()}
							>
								{t("share.send")}
							</button>
						</motion.div>
					</div>
				</>
			)}
		</AnimatePresence>
	);
}
